import * as React from "react";

import { useModal } from "@editor/hooks/useModal";

import IconButton from "@replo/design-system/components/button/IconButton";
import Separator from "@replo/design-system/components/Separator";
import cn from "@replo/design-system/utils/twMerge";
import Blockquote from "@tiptap/extension-blockquote";
import BulletList from "@tiptap/extension-bullet-list";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo,
  Strikethrough,
  Undo,
} from "lucide-react";

interface MinimalTiptapProps {
  content?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
}
const MinimalTiptap: React.FC<MinimalTiptapProps> = ({
  content = "",
  onChange,
  placeholder = "Start typing...",
  editable = true,
  className,
}) => {
  const { openModal } = useModal();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
          HTMLAttributes: {
            class: "font-bold",
          },
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline hover:text-blue-800",
        },
      }),
      Blockquote.configure({
        HTMLAttributes: {
          class: "border-l-4 border-gray-300 pl-2 italic",
        },
      }),
      BulletList.configure({
        keepMarks: true,
        keepAttributes: false,
        itemTypeName: "listItem",
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: false,
        itemTypeName: "listItem",
      }),
      ListItem,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl focus:outline-none",
          "min-h-[70.5px] p-4 border-0",
          "prose-ul:list-disc prose-ol:list-decimal prose-li:ml-4",
          "[&_ol]:list-decimal [&_ul]:list-disc [&_li]:ml-4",
          "prose-blockquote:border-l-2 prose-blockquote:border-gray-300 prose-blockquote:pl-2 prose-blockquote:italic",
          "prose-headings:font-bold [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg",
        ),
      },
    },
  });

  const openLinkModal = React.useCallback(() => {
    if (!editor) {
      return;
    }
    const previousUrl = editor.getAttributes("link").href;
    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
    );

    openModal({
      type: "minimalTipTapLinkEditorModal",
      props: {
        linkUrl: previousUrl ?? "",
        linkText: selectedText ?? "",
        onSave: (url: string, text: string) => {
          if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
          } else {
            const displayText = text ?? url;

            // If there's selected text, replace it with the link text and apply link mark
            if (editor.state.selection.from !== editor.state.selection.to) {
              editor
                .chain()
                .focus()
                .deleteSelection()
                .insertContent(displayText)
                .setTextSelection({
                  from: editor.state.selection.from,
                  to: editor.state.selection.from + displayText.length,
                })
                .setLink({ href: url })
                .run();
            } else {
              // If no text selected, insert the text and apply link mark
              const currentPos = editor.state.selection.from;
              editor
                .chain()
                .focus()
                .insertContent(displayText)
                .setTextSelection({
                  from: currentPos,
                  to: currentPos + displayText.length,
                })
                .setLink({ href: url })
                .run();
            }
          }
        },
      },
    });
  }, [editor, openModal]);

  if (!editor) {
    return null;
  }
  return (
    <div
      className={cn(
        "border-0.5 border-border-component rounded-lg overflow-hidden bg-sheet",
        className,
      )}
    >
      <div className="border-b-0.5 border-border-component p-1 flex flex-wrap items-center gap-1">
        <IconButton
          variant="tertiary"
          isActive={editor.isActive("bold")}
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          icon={<Bold className="h-4 w-4 text-muted" />}
          aria-label="Toggle bold"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("italic")}
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          icon={<Italic className="h-4 w-4 text-muted" />}
          aria-label="Toggle italic"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("strike")}
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          icon={<Strikethrough className="h-4 w-4 text-muted" />}
          aria-label="Toggle strikethrough"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("code")}
          size="sm"
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          icon={<Code className="h-4 w-4 text-muted" />}
          aria-label="Toggle code"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("link")}
          size="sm"
          onClick={openLinkModal}
          icon={<LinkIcon className="h-4 w-4 text-muted" />}
          aria-label="Set link"
        />
        <Separator orientation="vertical" className="h-6" />
        <IconButton
          variant="tertiary"
          isActive={editor.isActive("heading", { level: 1 })}
          size="sm"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          disabled={
            !editor.can().chain().focus().toggleHeading({ level: 1 }).run()
          }
          icon={<Heading1 className="h-4 w-4 text-muted" />}
          aria-label="Toggle heading 1"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("heading", { level: 2 })}
          size="sm"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          disabled={
            !editor.can().chain().focus().toggleHeading({ level: 2 }).run()
          }
          icon={<Heading2 className="h-4 w-4 text-muted" />}
          aria-label="Toggle heading 2"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("heading", { level: 3 })}
          size="sm"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          disabled={
            !editor.can().chain().focus().toggleHeading({ level: 3 }).run()
          }
          icon={<Heading3 className="h-4 w-4 text-muted" />}
          aria-label="Toggle heading 3"
        />
        <Separator orientation="vertical" className="h-6" />
        <IconButton
          variant="tertiary"
          isActive={editor.isActive("bulletList")}
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={!editor.can().chain().focus().toggleBulletList().run()}
          icon={<List className="h-4 w-4 text-muted" />}
          aria-label="Toggle bullet list"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("orderedList")}
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={!editor.can().chain().focus().toggleOrderedList().run()}
          icon={<ListOrdered className="h-4 w-4 text-muted" />}
          aria-label="Toggle ordered list"
        />

        <IconButton
          variant="tertiary"
          isActive={editor.isActive("blockquote")}
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={!editor.can().chain().focus().toggleBlockquote().run()}
          icon={<Quote className="h-4 w-4 text-muted" />}
          aria-label="Toggle blockquote"
        />
        <Separator orientation="vertical" className="h-6" />
        <IconButton
          variant="tertiary"
          isActive={false}
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          icon={<Minus className="h-4 w-4 text-muted" />}
          aria-label="Insert horizontal rule"
        />
        <Separator orientation="vertical" className="h-6" />
        <IconButton
          variant="tertiary"
          isActive={false}
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          icon={<Undo className="h-4 w-4 text-muted" />}
          aria-label="Undo"
        />

        <IconButton
          variant="tertiary"
          isActive={false}
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          icon={<Redo className="h-4 w-4 text-muted" />}
          aria-label="Redo"
        />
      </div>

      <div className="overflow-y-auto max-h-[210px]">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
};
export { MinimalTiptap };
